import type { Ref } from 'vue';
import { watchEffect } from 'vue';
import type {
  FieldHtmlNameType,
  HekateDisplayField,
  HekateDisplayForm,
  HekateFormDefinition,
  HekateEventBusCallbackParams,
} from '@onshape/www-inertia';
import { FieldHtmlNameUtil, OptionListUtil, ErrorTagUtil } from '@onshape/www-inertia';

import ExternalUtil from '../../../ExternalUtil';
import hekateBus from '../../../../bus';
import ErrorUtil from '../../../util/ErrorUtil';
import type { ProgressiveProfileDefinition } from './ProgressiveProfileDefinition';
import type { ProgressRule } from './ProgressRule';
import type { FormSubmitResolution } from '../../../../model/HekateFormSubmission';

export default abstract class BaseProgressiveProfileDefinition implements ProgressiveProfileDefinition {
  /** Fields values that should be stored and pre-filled */
  private readonly basePreFilledFields: readonly FieldHtmlNameType[] = [
    FieldHtmlNameUtil.firstName,
    FieldHtmlNameUtil.lastName,
    FieldHtmlNameUtil.email,
    FieldHtmlNameUtil.pNumber,
    FieldHtmlNameUtil.phoneNumber,
    FieldHtmlNameUtil.companyName,
    FieldHtmlNameUtil.jobTitle,
    FieldHtmlNameUtil.privacyConsentAccepted,
    FieldHtmlNameUtil.maxCountry,
    FieldHtmlNameUtil.maxState,
    FieldHtmlNameUtil.maxCity,
  ];

  abstract getProgressRules(): readonly ProgressRule[];

  abstract getBranchField(): FieldHtmlNameType;

  abstract getAlwaysShownFields(): readonly FieldHtmlNameType[];

  public onSuccess({
    redirectUrl,
  }: {
    formDefinition: Ref<HekateDisplayForm>;
    redirectUrl: string | null;
    formSubmitResolution?: Promise<FormSubmitResolution | null>;
  }): void {
    if (redirectUrl) {
      // The timeout is to allow the `dataLayer.push` to fire.
      setTimeout(() => {
        document.location.href = redirectUrl;
      }, 850);
    }
  }

  public onEloquaDown({
    formDefinition,
  }: {
    formDefinition: Ref<HekateFormDefinition>;
    redirectUrl: string | null;
  }): void {
    ErrorUtil.safePostMessageToSentry({
      tag: ErrorTagUtil.eloquaDown,
      msg: `${formDefinition.value.name} (${formDefinition.value.id})`,
    });
  }

  public getPreFilledFields(): readonly FieldHtmlNameType[] {
    return this.basePreFilledFields;
  }

  /** Make sure all fields that are used in the definition are in `fields` */
  public isAllFormRequiredFieldsPresent(fields: HekateDisplayField[]): string[] {
    const fieldsToCheck: { [key: string]: boolean } = {};
    fieldsToCheck[this.getBranchField()] = false;
    this.getAlwaysShownFields().forEach((field) => {
      fieldsToCheck[field] = false;
    });
    this.getProgressRules().forEach((rule) => {
      rule.fieldToShow.forEach((field) => {
        fieldsToCheck[field] = false;
      });
    });

    fields.forEach((field) => {
      fieldsToCheck[field.htmlName] = true;
    });

    const missing = Object.entries(fieldsToCheck)
      .filter(([, vv]) => !vv)
      .map(([kk]) => kk);

    return missing;
  }

  public realtimeBranchModifications({
    formDefinition,
  }: {
    formDefinition: Ref<HekateDisplayForm>;
  }): void {
    hekateBus.addEventListener(
      'hide-show-form',
      (evt: HekateEventBusCallbackParams<{ formId: string; action: 'hide' | 'show' }>) => {
        if (evt.data.formId !== formDefinition.value.id) {
          return;
        }
        if (evt.data.action === 'hide') {
          formDefinition.value.isHidden = true;
        }
        if (evt.data.action === 'show') {
          formDefinition.value.isHidden = false;
        }
      }
    );

    BaseProgressiveProfileDefinition.watchCountries({ formDefinition });
  }

  private static watchCountries({ formDefinition }: { formDefinition: Ref<HekateDisplayForm> }) {
    watchEffect(() => {
      if (formDefinition.value === null) {
        return;
      }
      const foundCountry = ExternalUtil.findDisplayFieldOrNull({
        formDefinition,
        fieldName: FieldHtmlNameUtil.maxCountry,
      });
      const foundState = formDefinition.value.displayFields.find(
        (field: HekateDisplayField) => field.htmlName === FieldHtmlNameUtil.maxState
      );
      if (!foundState || !foundCountry) {
        return;
      }

      if (
        foundCountry.componentName === 'hidden-field' ||
        foundState.componentName === 'hidden-field'
      ) {
        return;
      }

      if (foundCountry.val === 'United States') {
        foundState.componentName = 'single-select-field';
        foundState.optionList.elements.splice(0, foundState.optionList.elements.length);
        OptionListUtil.usStates.forEach((state) => {
          foundState.optionList.elements.push(state);
        });
      } else if (foundCountry.val === 'Canada') {
        foundState.componentName = 'single-select-field';
        foundState.optionList.elements.splice(0, foundState.optionList.elements.length);
        OptionListUtil.canadaProvinces.forEach((province) => {
          foundState.optionList.elements.push(province);
        });
      } else {
        foundState.optionList.elements.splice(0, foundState.optionList.elements.length);
        foundState.componentName = 'text-input-field';
      }
      foundState.notifyChange();
    });
  }
}
